"use client";

import React, { useState, useEffect } from "react";

const apiUrl = (process.env.NEXT_PUBLIC_API_URL || "")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");

export default function BlogComments({ blogId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    comment: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch comments
  const fetchComments = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/comment/blog/${blogId}`, {
        cache: "no-store",
      });
      
      const contentType = res.headers.get("content-type");
      if (res.ok && contentType && contentType.includes("application/json")) {
        const data = await res.json();
        if (data.success) {
          setComments(data.data);
        }
      } else {
        console.warn("Backend API not found or returned non-JSON. Please deploy backend changes.");
      }
    } catch (error) {
      console.error("Failed to fetch comments", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (blogId) {
      fetchComments();
    }
  }, [blogId]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);

    try {
      const res = await fetch(`${apiUrl}/api/comment/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, blogId }),
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("API returned non-JSON response. Please ensure backend is deployed.");
      }

      const data = await res.json();

      if (data.success) {
        setMessage({ type: "success", text: "Comment posted successfully!" });
        setFormData({ name: "", email: "", comment: "" });
        // Refresh the comment list
        fetchComments();
      } else {
        setMessage({ type: "error", text: data.message || "Failed to post comment" });
      }
    } catch (error) {
      console.error("Error posting comment", error);
      setMessage({ type: "error", text: "API Error: Please deploy the backend changes to enable comments." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="v6-comments-section mt-5 pt-5">
      <h3 className="v6-section-title mb-4">COMMENTS ({comments.length})</h3>

      <div 
        className="comments-list custom-scrollbar" 
        style={comments.length > 6 ? { maxHeight: "550px", overflowY: "auto", paddingRight: "15px" } : {}}
      >
        {loading ? (
          <p className="text-secondary">Loading comments...</p>
        ) : comments.length === 0 ? (
          <p className="text-secondary">No comments yet. Be the first to share your thoughts!</p>
        ) : (
          comments.map((c) => (
            <div className="comment-item" key={c._id}>
              <div className="comment-avatar">
                {c.name.charAt(0).toUpperCase()}
              </div>
              <div className="comment-content">
                <h5>{c.name}</h5>
                <span className="comment-date">
                  {new Date(c.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <p>{c.comment}</p>
                {/* <button className="comment-reply-btn">Reply</button> */}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="comment-form-box mt-5">
        <h4>Leave a Reply</h4>
        {message && (
          <div
            className={`alert ${
              message.type === "success" ? "alert-success" : "alert-danger"
            }`}
            role="alert"
          >
            {message.text}
          </div>
        )}
        <form className="comment-form row g-3" onSubmit={handleSubmit}>
          <div className="col-md-6">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              placeholder="Your Name *"
              autoComplete="name"
              required
            />
          </div>
          <div className="col-md-6">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-control"
              placeholder="Your Email *"
              autoComplete="email"
              required
            />
          </div>
          <div className="col-12">
            <textarea
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              className="form-control"
              rows="5"
              placeholder="Your Comment *"
              required
            ></textarea>
          </div>
          <div className="col-12 mt-4">
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary-orange px-4 py-2"
              style={{ width: "auto" }}
            >
              {submitting ? "Posting..." : "Post Comment"}
              <i className="fa-regular fa-paper-plane ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
